import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Loader2, ShieldCheck, History, Lock, KeyRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { safeSS } from "@/lib/safeStorage";

const norm = (e) => (e || "").trim().toLowerCase();
const ELEV_KEY = "tm_elev";
const elevHeaders = () => ({ "X-Elevated-Token": safeSS.getItem(ELEV_KEY) || "" });
const errCode = (err) => err?.response?.data?.detail?.code;

export default function ModuleAccessPanel({ module = "granos" }) {
  const [phase, setPhase] = useState("loading"); // loading | no_access | otp | ready
  const [emails, setEmails] = useState([]);
  const [meta, setMeta] = useState({});
  const [denied, setDenied] = useState([]);
  const [supers, setSupers] = useState([]);
  const [me, setMe] = useState("");
  const [audit, setAudit] = useState([]);
  const [secDenied, setSecDenied] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [nuevoSuper, setNuevoSuper] = useState("");
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const handle403 = (err) => {
    const c = errCode(err);
    if (c === "needs_otp") { safeSS.removeItem(ELEV_KEY); setPhase("otp"); return true; }
    if (c === "no_access") { setPhase("no_access"); return true; }
    return false;
  };

  const loadData = useCallback(async () => {
    try {
      const [a, d, s, au] = await Promise.all([
        api.get("/admin/module-access", { params: { module }, headers: elevHeaders() }),
        api.get("/admin/access-denied", { params: { module }, headers: elevHeaders() }),
        api.get("/superadmin/list", { headers: elevHeaders() }),
        api.get("/superadmin/audit", { headers: elevHeaders() }),
      ]);
      setEmails(a.data.emails || []);
      setMeta({ updated_by: a.data.updated_by, updated_at: a.data.updated_at });
      setDenied(d.data.denied || []);
      setSupers(s.data.superadmins || []);
      setMe(s.data.me || "");
      setAudit(au.data.audit || []);
      setSecDenied(au.data.denied || []);
      setPhase("ready");
    } catch (err) {
      if (!handle403(err)) toast.error("No se pudo cargar la sección.");
    }
  }, [module]);

  const checkStatus = useCallback(async () => {
    try {
      const r = await api.get("/superadmin/status", { headers: elevHeaders() });
      if (!r.data?.is_superadmin) return setPhase("no_access");
      if (r.data?.elevated) return loadData();
      setPhase("otp");
    } catch { setPhase("no_access"); }
  }, [loadData]);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const requestCode = async () => {
    setSending(true);
    try {
      await api.post("/superadmin/otp/request", {});
      toast.success("Te enviamos un código a tu correo.");
    } catch (err) {
      if (!handle403(err)) toast.error(err?.response?.data?.detail || "No se pudo enviar el código.");
    } finally { setSending(false); }
  };

  const verifyCode = async () => {
    if (!code.trim()) return toast.error("Ingresá el código que recibiste.");
    setSaving(true);
    try {
      const r = await api.post("/superadmin/otp/verify", { code: code.trim() });
      safeSS.setItem(ELEV_KEY, r.data.token);
      setCode("");
      toast.success("Verificación correcta.");
      await loadData();
    } catch (err) {
      if (!handle403(err)) toast.error(err?.response?.data?.detail || "Código incorrecto.");
    } finally { setSaving(false); }
  };

  const persist = async (list) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/module-access", { module, emails: list }, { headers: elevHeaders() });
      setEmails(r.data.emails || []);
      setMeta({ updated_by: r.data.updated_by, updated_at: r.data.updated_at });
      toast.success("Lista de acceso actualizada.");
    } catch (err) {
      if (!handle403(err)) toast.error("No se pudo guardar la lista.");
    } finally { setSaving(false); }
  };

  const add = () => {
    const e = norm(nuevo);
    if (!e || !e.includes("@")) return toast.error("Ingresá un correo válido.");
    if (emails.includes(e)) return toast.error("Ese correo ya está en la lista.");
    setNuevo("");
    persist([...emails, e]);
  };
  const remove = (e) => persist(emails.filter((x) => x !== e));

  const addSuper = async () => {
    const e = norm(nuevoSuper);
    if (!e || !e.includes("@")) return toast.error("Ingresá un correo válido.");
    setSaving(true);
    try {
      const r = await api.post("/superadmin/list", { email: e }, { headers: elevHeaders() });
      setSupers(r.data.superadmins || []);
      setNuevoSuper("");
      toast.success("Superadministrador agregado.");
    } catch (err) {
      if (!handle403(err)) toast.error(err?.response?.data?.detail || "No se pudo agregar.");
    } finally { setSaving(false); }
  };

  const removeSuper = async (e) => {
    setSaving(true);
    try {
      const r = await api.delete(`/superadmin/list/${encodeURIComponent(e)}`, { headers: elevHeaders() });
      setSupers(r.data.superadmins || []);
      toast.success("Superadministrador quitado.");
    } catch (err) {
      if (!handle403(err)) toast.error(err?.response?.data?.detail || "No se pudo quitar.");
    } finally { setSaving(false); }
  };

  if (phase === "loading")
    return <div className="py-6 text-center text-slate-400" data-testid="module-access-loading"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;

  if (phase === "no_access")
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center" data-testid="module-access-no-access">
        <ShieldAlert className="mx-auto h-8 w-8 text-red-600 mb-2" />
        <p className="font-semibold text-red-900">No tenés acceso a esta sección</p>
        <p className="text-xs text-red-500 mt-1">Esta sección es exclusiva del superadministrador.</p>
      </div>
    );

  if (phase === "otp")
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="module-access-otp">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 mb-1">
          <Lock className="h-4 w-4 text-emerald-700" /> Verificación de seguridad
        </div>
        <p className="text-xs text-slate-500 mb-3">Para abrir "Acceso a módulos" enviamos un código a tu correo. La sesión se cierra tras 15 min de inactividad.</p>
        <div className="flex gap-2">
          <Input data-testid="otp-code-input" value={code} onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verifyCode()} placeholder="Código de 6 dígitos" className="h-9" inputMode="numeric" />
          <Button data-testid="otp-verify-btn" onClick={verifyCode} disabled={saving} className="gap-1.5 bg-emerald-700 hover:bg-emerald-800">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Verificar
          </Button>
        </div>
        <Button data-testid="otp-request-btn" variant="link" onClick={requestCode} disabled={sending} className="px-0 mt-1 h-auto text-emerald-700">
          {sending ? "Enviando…" : "Enviar código a mi correo"}
        </Button>
      </div>
    );

  return (
    <div data-testid="module-access-panel">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-emerald-900">
        <ShieldCheck className="h-4 w-4 text-emerald-700" /> Lista 1 · Granos solamente
      </div>
      <p className="mb-3 text-xs text-slate-400">
        Solo estos correos (verificados) pueden ver y usar el módulo Granos (web, app y bot).
        {meta.updated_by && <> · Último cambio: <strong>{meta.updated_by}</strong> {meta.updated_at ? `(${new Date(meta.updated_at).toLocaleString("es-PY")})` : ""}</>}
      </p>

      <div className="mb-3 flex gap-2">
        <Input data-testid="module-access-new-email" value={nuevo} onChange={(e) => setNuevo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} placeholder="correo@tecnomyl.com" className="h-9" />
        <Button data-testid="module-access-add" onClick={add} disabled={saving} className="gap-1.5 bg-emerald-700 hover:bg-emerald-800">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Agregar
        </Button>
      </div>

      <ul className="space-y-1.5" data-testid="module-access-list">
        {emails.map((e) => (
          <li key={e} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={`module-access-row-${e}`}>
            <span className="font-medium text-slate-700">{e}</span>
            <button onClick={() => remove(e)} disabled={saving} className="text-red-500 hover:text-red-700" data-testid={`module-access-remove-${e}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
        {emails.length === 0 && <li className="py-4 text-center text-sm text-slate-400">Lista vacía — nadie tendrá acceso a Granos.</li>}
      </ul>

      <div className="mt-6 border-t border-slate-100 pt-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-900"><ShieldAlert className="h-4 w-4 text-emerald-700" /> Superadministradores</div>
        <p className="mb-2 text-xs text-slate-400">Solo un superadministrador puede ver y editar esta sección. No se puede quitar al último.</p>
        <div className="mb-3 flex gap-2">
          <Input data-testid="superadmin-new-email" value={nuevoSuper} onChange={(e) => setNuevoSuper(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSuper()} placeholder="correo de respaldo" className="h-9" />
          <Button data-testid="superadmin-add" onClick={addSuper} disabled={saving} className="gap-1.5 bg-emerald-700 hover:bg-emerald-800">
            <Plus className="h-4 w-4" /> Agregar
          </Button>
        </div>
        <ul className="space-y-1.5" data-testid="superadmin-list">
          {supers.map((e) => (
            <li key={e} className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-sm" data-testid={`superadmin-row-${e}`}>
              <span className="font-medium text-emerald-900">{e}{e === me && <span className="ml-2 text-[11px] text-emerald-600">(vos)</span>}</span>
              <button onClick={() => removeSuper(e)} disabled={saving || supers.length <= 1} className="text-red-500 hover:text-red-700 disabled:opacity-30" data-testid={`superadmin-remove-${e}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><History className="h-4 w-4 text-slate-500" /> Intentos denegados ({denied.length + secDenied.length})</div>
        <div className="max-h-48 overflow-auto rounded-lg border border-slate-100" data-testid="module-access-denied">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-slate-500"><tr><th className="px-2 py-1 text-left">Fecha</th><th className="px-2 py-1 text-left">Usuario</th><th className="px-2 py-1 text-left">Endpoint</th></tr></thead>
            <tbody>
              {[...secDenied, ...denied].length === 0 ? (
                <tr><td colSpan={3} className="px-2 py-4 text-center text-slate-400">Sin intentos denegados.</td></tr>
              ) : [...secDenied, ...denied].map((d, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1 whitespace-nowrap text-slate-500">{d.at ? new Date(d.at).toLocaleString("es-PY") : "—"}</td>
                  <td className="px-2 py-1">{d.email || "—"}</td>
                  <td className="px-2 py-1 font-mono text-slate-500">{d.method} {d.path}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><History className="h-4 w-4 text-slate-500" /> Historial de cambios ({audit.length})</div>
        <div className="max-h-48 overflow-auto rounded-lg border border-slate-100" data-testid="superadmin-audit">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-slate-500"><tr><th className="px-2 py-1 text-left">Fecha</th><th className="px-2 py-1 text-left">Quién</th><th className="px-2 py-1 text-left">Acción</th></tr></thead>
            <tbody>
              {audit.length === 0 ? (
                <tr><td colSpan={3} className="px-2 py-4 text-center text-slate-400">Sin cambios registrados.</td></tr>
              ) : audit.map((a, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-2 py-1 whitespace-nowrap text-slate-500">{a.at ? new Date(a.at).toLocaleString("es-PY") : "—"}</td>
                  <td className="px-2 py-1">{a.actor || "—"}</td>
                  <td className="px-2 py-1 text-slate-600">{a.action}{a.target ? ` · ${a.target}` : ""}{a.detail?.added?.length ? ` · +${a.detail.added.join(", ")}` : ""}{a.detail?.removed?.length ? ` · -${a.detail.removed.join(", ")}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
